import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SystemButton } from './ActionButton';
import { dealTokens } from './designTokens';

// Inline month calendar of the Deal design system — the date grid of the reservation flow
// (single reservation "Data" step, multiple reservation period). Monday-first, round day
// buttons, primary fill for the selected day(s) and a soft band for a range in between.
//
// Controlled: the visible `month` (any Date inside it) is owned by the caller and changed
// through `onMonthChange`. Days outside the month are left blank. Per-day state comes from
// the predicates, so callers can express availability, a single date or a range:
//   isSelected(date) → filled day      isInRange(date) → soft band
//   isDisabled(date) → not clickable   onSelect(date)  → click on an enabled day
const SystemMonthCalendar = ({
  month,
  onMonthChange,
  onSelect,
  isSelected = () => false,
  isInRange = () => false,
  isMarked = () => false,
  isDisabled = () => false,
  rangeStart,
  rangeEnd,
  onRangeEndpointChange,
  canPrevious = true,
  canNext = true,
}) => {
  const { t, i18n } = useTranslation();
  const monthStart = new Date(month.getFullYear(), month.getMonth(), 1);
  const leading = (monthStart.getDay() + 6) % 7;
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cellCount = Math.ceil((leading + daysInMonth) / 7) * 7;
  const cells = Array.from({ length: cellCount }, (_, index) => new Date(month.getFullYear(), month.getMonth(), index - leading + 1));
  const dateKey = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const hasRange = Boolean(rangeStart && rangeEnd);
  const dragRef = useRef(null);
  const suppressClickRef = useRef(false);
  const [dragPreview, setDragPreview] = useState(null);
  const visibleStart = dragPreview?.endpoint === 'start' ? dragPreview.date : String(rangeStart).slice(0, 10);
  const visibleEnd = dragPreview?.endpoint === 'end' ? dragPreview.date : String(rangeEnd).slice(0, 10);
  const keyToDate = key => {
    const [year, monthIndex, day] = key.split('-').map(Number);
    return new Date(year, monthIndex - 1, day);
  };
  const beginRangeDrag = (event, key, isStart, isEnd) => {
    if (!onRangeEndpointChange || (!isStart && !isEnd)) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const endpoint = isStart && isEnd ? (event.clientX < (bounds.left + bounds.right) / 2 ? 'start' : 'end') : isStart ? 'start' : 'end';
    dragRef.current = { endpoint, pointerId: event.pointerId, startKey: key, currentKey: key, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const updateRangeDrag = event => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const target = document.elementFromPoint?.(event.clientX, event.clientY)?.closest?.('[data-calendar-date]');
    const key = target?.dataset.calendarDate;
    if (!key || !event.currentTarget.contains(target)) return;
    const bounded =
      drag.endpoint === 'start' && key > String(rangeEnd).slice(0, 10)
        ? String(rangeEnd).slice(0, 10)
        : drag.endpoint === 'end' && key < String(rangeStart).slice(0, 10)
        ? String(rangeStart).slice(0, 10)
        : key;
    if (bounded === drag.currentKey) return;
    drag.currentKey = bounded;
    drag.moved = true;
    setDragPreview({ endpoint: drag.endpoint, date: bounded });
  };
  const finishRangeDrag = event => {
    const drag = dragRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    if (drag.moved) {
      onRangeEndpointChange(drag.endpoint, keyToDate(drag.currentKey));
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    }
    dragRef.current = null;
    setDragPreview(null);
  };
  const cancelRangeDrag = () => {
    dragRef.current = null;
    setDragPreview(null);
  };

  return (
    <div
      onPointerMove={updateRangeDrag}
      onPointerUp={finishRangeDrag}
      onPointerCancel={cancelRangeDrag}
      style={{
        boxSizing: 'border-box',
        minWidth: 0,
        border: `1px solid ${dealTokens.color.border}`,
        borderRadius: dealTokens.radius.lg,
        padding: dealTokens.space[3],
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: dealTokens.space[3] }}>
        <SystemButton
          type="button"
          variant="ghost"
          aria-label={t('GENERAL.previous', 'Precedente')}
          disabled={!canPrevious}
          onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          style={{ minWidth: 32, width: 32, padding: 0 }}
        >
          ‹
        </SystemButton>
        <strong style={{ color: dealTokens.color.text, fontSize: 14, textTransform: 'capitalize' }}>
          {month.toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' })}
        </strong>
        <SystemButton
          type="button"
          variant="ghost"
          aria-label={t('GENERAL.next', 'Successivo')}
          disabled={!canNext}
          onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          style={{ minWidth: 32, width: 32, padding: 0 }}
        >
          ›
        </SystemButton>
      </div>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', justifyItems: 'center', rowGap: 4, width: '100%', minWidth: 0 }}
      >
        {Array.from({ length: 7 }, (_, index) => new Date(2026, 0, 5 + index)).map(day => (
          <div
            key={day.getDay()}
            style={{ width: '100%', minWidth: 0, textAlign: 'center', color: dealTokens.color.textMuted, fontSize: 10, paddingBottom: 4 }}
          >
            {day.toLocaleDateString(i18n.language, { weekday: 'short' }).replace('.', '')}
          </div>
        ))}
        {cells.map(date => {
          const outsideMonth = date.getMonth() !== month.getMonth();
          if (outsideMonth) return <div key={date.getTime()} aria-hidden="true" style={{ height: 38 }} />;
          const disabled = isDisabled(date);
          const key = dateKey(date);
          const rangeStartDay = hasRange && key === visibleStart;
          const rangeEndDay = hasRange && key === visibleEnd;
          const selected = hasRange ? rangeStartDay || rangeEndDay : isSelected(date);
          const inRange = hasRange ? key > visibleStart && key < visibleEnd : !selected && isInRange(date);
          const marked = isMarked(date);
          const withinRange = hasRange && (selected || inRange);
          return (
            <div
              key={date.getTime()}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                background: inRange || withinRange ? dealTokens.color.primarySoft : 'transparent',
                borderRadius: rangeStartDay ? '50% 0 0 50%' : rangeEndDay ? '0 50% 50% 0' : 0,
              }}
            >
              <button
                type="button"
                data-calendar-date={key}
                disabled={disabled}
                aria-pressed={selected}
                onPointerDown={event => beginRangeDrag(event, key, rangeStartDay, rangeEndDay)}
                onClick={() => {
                  if (suppressClickRef.current) {
                    suppressClickRef.current = false;
                    return;
                  }
                  onSelect(date);
                }}
                style={{
                  position: 'relative',
                  width: 'min(38px, 100%)',
                  minWidth: 0,
                  height: 38,
                  border: selected || (!hasRange && marked) ? `1px solid ${dealTokens.color.primary}` : '1px solid transparent',
                  borderRadius: '50%',
                  background: selected ? dealTokens.color.primary : !hasRange && marked ? dealTokens.color.primarySoft : 'transparent',
                  color: selected ? dealTokens.color.surface : disabled ? dealTokens.color.textSubtle : dealTokens.color.text,
                  opacity: disabled ? 0.4 : 1,
                  cursor: disabled ? 'default' : dragPreview && selected ? 'grabbing' : selected && onRangeEndpointChange ? 'grab' : 'pointer',
                  touchAction: selected && onRangeEndpointChange ? 'none' : 'auto',
                  fontFamily: dealTokens.font.family,
                  fontSize: 12,
                  fontWeight: selected ? dealTokens.font.weightSemibold : dealTokens.font.weightRegular,
                }}
              >
                {date.getDate()}
                {hasRange && marked && !selected && (
                  <span
                    aria-hidden="true"
                    style={{
                      position: 'absolute',
                      bottom: 3,
                      left: '50%',
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      background: dealTokens.color.primary,
                      transform: 'translateX(-50%)',
                    }}
                  />
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SystemMonthCalendar;
